import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, MapPin } from "lucide-react";
import data from "@/data/stockists.json";

const TASTING_ROOMS = data.tasting_rooms;
const VISITS = data.visits;

const TABS = [
  { id: "rooms", label: "Tasting Rooms", testId: "stockists-tab-rooms" },
  { id: "visits", label: "Upcoming Visits", testId: "stockists-tab-visits" },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};
const FADE_UP_DELAYED = { ...fadeUp.transition, delay: 0.15 };

// Hoisted style objects — module-scope = stable identity across renders.
const STYLE_HERO_TITLE = {
  color: "var(--noor-bone)",
  fontSize: "clamp(2.8rem, 5.6vw, 6.4rem)",
  letterSpacing: "-0.025em",
};
const STYLE_HERO_BODY = { color: "var(--text-secondary)" };
const STYLE_BACK_LINK = { color: "var(--text-secondary)", letterSpacing: "0.32em" };
const STYLE_ROSE_ACCENT = { color: "var(--accent-rose)" };
const STYLE_TAB_LABEL = { letterSpacing: "0.32em" };
const STYLE_DATE_LABEL = {
  color: "var(--accent-rose)",
  letterSpacing: "0.32em",
  textTransform: "uppercase",
};
const STYLE_DATE_TIME = {
  color: "var(--text-secondary)",
  letterSpacing: "0.28em",
  textTransform: "uppercase",
};
const STYLE_COUNTRY_META = {
  color: "var(--text-secondary)",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
};
const STYLE_CTA_BTN = { padding: "0.7rem 1.2rem", fontSize: "0.66rem" };

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function groupByCity(items) {
  return items.reduce((acc, item) => {
    const key = item.city;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

function StockistsBackLink() {
  return (
    <Link
      data-testid="stockists-back"
      to="/"
      className="inline-flex items-center gap-2 font-mono text-[10px] uppercase mb-12 transition-colors"
      style={STYLE_BACK_LINK}
    >
      <ArrowLeft size={12} />
      Return to the House
    </Link>
  );
}

function tabButtonStyle(isActive) {
  return {
    letterSpacing: "0.32em",
    background: isActive ? "var(--accent-rose)" : "transparent",
    color: isActive ? "var(--noor-obsidian)" : "var(--text-secondary)",
    border: `1px solid ${isActive ? "var(--accent-rose)" : "var(--border-subtle)"}`,
  };
}

function StockistsTabs({ active, onChangeTab }) {
  return (
    <nav data-testid="stockists-tabs" className="mt-14 flex gap-3 flex-wrap">
      {TABS.map((t) => (
        <button
          key={t.id}
          data-testid={t.testId}
          onClick={() => onChangeTab(t.id)}
          className="font-mono text-[10px] uppercase px-4 py-3 transition-all"
          style={tabButtonStyle(active === t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}

function StockistsHero({ active, onChangeTab }) {
  return (
    <header
      data-testid="stockists-hero"
      className="max-w-[1480px] mx-auto px-6 md:px-10 pt-44 pb-16 md:pb-24"
    >
      <StockistsBackLink />
      <p className="overline mb-5" style={STYLE_ROSE_ACCENT}>
        VII · Where to find
      </p>
      <motion.h1
        {...fadeUp}
        className="font-display leading-[1.02] max-w-[20ch]"
        style={STYLE_HERO_TITLE}
      >
        The House <em className="italic" style={STYLE_ROSE_ACCENT}>travels.</em>
      </motion.h1>
      <motion.p
        {...fadeUp}
        transition={FADE_UP_DELAYED}
        className="font-display-light mt-10 max-w-[58ch] text-lg md:text-xl leading-[1.65]"
        style={STYLE_HERO_BODY}
      >
        NOOR isn&rsquo;t on a shelf. It is poured in a small number of rooms that take
        spirits seriously, and at a small number of private evenings each season.
      </motion.p>
      <StockistsTabs active={active} onChangeTab={onChangeTab} />
    </header>
  );
}

function TastingRoomCard({ room }) {
  return (
    <article
      data-testid={`stockist-room-${room.id}`}
      className="liquid-glass p-7 md:p-8 flex flex-col gap-3"
    >
      <p className="overline" style={{ color: "var(--accent-rose)", fontSize: "0.62rem" }}>
        {room.city} · {room.country}
      </p>
      <h3 className="font-display text-2xl md:text-[1.75rem] leading-tight" style={{ color: "var(--noor-bone)" }}>
        {room.venue}
      </h3>
      <p className="font-mono text-[10px]" style={{ color: "var(--text-secondary)", letterSpacing: "0.22em", textTransform: "uppercase" }}>
        <MapPin size={10} className="inline mr-2" strokeWidth={1.4} />
        {room.neighbourhood}
      </p>
      <p className="text-sm leading-relaxed mt-1" style={{ color: "var(--text-secondary)" }}>
        {room.note}
      </p>
      <p
        className="font-mono text-[9px] mt-3"
        style={{
          color: room.by_appointment ? "var(--accent-rose)" : "var(--text-secondary)",
          letterSpacing: "0.28em", textTransform: "uppercase",
        }}
      >
        {room.by_appointment ? "By appointment" : "Walk-in welcome"}
      </p>
    </article>
  );
}

function TastingRoomsView() {
  const grouped = useMemo(() => groupByCity(TASTING_ROOMS), []);
  const cities = Object.keys(grouped).sort();

  return (
    <section
      data-testid="stockists-rooms"
      className="max-w-[1480px] mx-auto px-6 md:px-10 pb-24 space-y-16"
    >
      <p
        data-testid="stockists-placeholder-notice"
        className="font-mono text-[9px] inline-block px-3 py-2"
        style={{
          color: "var(--accent-rose)",
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          border: "1px solid rgba(201,164,122,0.32)",
          background: "rgba(201,164,122,0.06)",
        }}
      >
        Editorial placeholder venues · final partner list confirmed at launch
      </p>
      {cities.map((city) => (
        <div key={city}>
          <h2
            className="font-display text-3xl md:text-4xl mb-8"
            style={{ color: "var(--noor-bone)" }}
          >
            {city}
          </h2>
          <div className="grid grid-cols-12 gap-y-6 md:gap-8">
            {grouped[city].map((room) => (
              <div key={room.id} className="col-span-12 md:col-span-6 lg:col-span-4">
                <TastingRoomCard room={room} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function VisitDate({ date, time }) {
  return (
    <div className="col-span-12 md:col-span-3">
      <p className="font-mono text-[10px] mb-2" style={STYLE_DATE_LABEL}>
        <Calendar size={11} className="inline mr-2" strokeWidth={1.4} />
        {formatDate(date)}
      </p>
      <p className="font-mono text-[10px]" style={STYLE_DATE_TIME}>
        {time}
      </p>
    </div>
  );
}

function VisitVenue({ visit }) {
  return (
    <div className="col-span-12 md:col-span-6">
      <h3 className="font-display text-2xl md:text-[1.75rem] leading-tight mb-1" style={{ color: "var(--noor-bone)" }}>
        {visit.city}
      </h3>
      <p className="font-mono text-[10px] mb-3" style={STYLE_COUNTRY_META}>
        {visit.country}
      </p>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        {visit.venue}
      </p>
    </div>
  );
}

function VisitSeats({ visit }) {
  const seatsLow = visit.seats_remaining <= 8;
  const seatsStyle = {
    color: seatsLow ? "var(--accent-rose)" : "var(--text-secondary)",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
  };
  return (
    <div className="col-span-12 md:col-span-3 flex flex-col items-start md:items-end gap-3">
      <p className="font-mono text-[10px]" style={seatsStyle}>
        {visit.seats_remaining} seats remain
      </p>
      <Link
        data-testid={`visit-cta-${visit.id}`}
        to="/inner-circle"
        className="btn-ghost"
        style={STYLE_CTA_BTN}
      >
        Request a seat <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function VisitCard({ visit }) {
  return (
    <article
      data-testid={`stockist-visit-${visit.id}`}
      className="liquid-glass p-8 md:p-10 grid grid-cols-12 gap-y-6 md:gap-8 items-center"
    >
      <VisitDate date={visit.date} time={visit.time} />
      <VisitVenue visit={visit} />
      <VisitSeats visit={visit} />
    </article>
  );
}

function VisitsView() {
  return (
    <section
      data-testid="stockists-visits"
      className="max-w-[1480px] mx-auto px-6 md:px-10 pb-24 space-y-6"
    >
      <p
        className="font-mono text-[9px] inline-block px-3 py-2 mb-4"
        style={{
          color: "var(--accent-rose)",
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          border: "1px solid rgba(201,164,122,0.32)",
          background: "rgba(201,164,122,0.06)",
        }}
      >
        Editorial placeholder dates · final calendar confirmed at launch
      </p>
      {VISITS.map((v) => (
        <VisitCard key={v.id} visit={v} />
      ))}
    </section>
  );
}

function StockistsFoot() {
  return (
    <section className="max-w-[1480px] mx-auto px-6 md:px-10 pb-32 pt-8">
      <div className="liquid-glass p-10 md:p-14 text-center" data-testid="stockists-foot">
        <p className="overline mb-4" style={{ color: "var(--accent-rose)" }}>
          Not on this list?
        </p>
        <p
          className="font-display italic mx-auto max-w-[58ch] mb-10"
          style={{ color: "var(--noor-bone)", fontSize: "clamp(1.4rem, 2.4vw, 2rem)", lineHeight: 1.4 }}
        >
          If you&rsquo;d like the House to visit your city, ask. We answer every note.
        </p>
        <Link to="/inner-circle" data-testid="stockists-cta-circle" className="btn-solid">
          Write to the House
        </Link>
      </div>
    </section>
  );
}

export default function Stockists() {
  const [tab, setTab] = useState("rooms");
  return (
    <main data-testid="stockists-page">
      <StockistsHero active={tab} onChangeTab={setTab} />
      {tab === "rooms" ? <TastingRoomsView /> : <VisitsView />}
      <StockistsFoot />
    </main>
  );
}
